import { useEffect, useState } from "react";
import {
    Button,
    Form,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

import type { ISearchMethod } from "Pages/Integration/EditIntegrations2/types";
import styles from "Pages/Integration/EditIntegrations2/EditIntegrations2.module.css";

const DIRECTIONS = ["get", "send", "link", "unlink", "compare", "report", "within", "to_both", "to_neither", ""];

function normalizeDirection(raw: string | null | undefined): string {
    if (!raw) return "";
    const t = raw.trim();
    return t.startsWith("Direction.") ? t.slice("Direction.".length) : t;
}

interface IProps {
    integration: IIntegration | null;
    existingGroups: string[];
    searchMethods: ISearchMethod[];
    onClose: () => void;
    onSave: (next: IIntegration) => Promise<void>;
}

interface IArgRow {
    name: string;
    data_source: string;
}

export default function EditIntegrationModal2({
    integration,
    existingGroups,
    searchMethods,
    onClose,
    onSave,
}: IProps) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [integrationGroup, setIntegrationGroup] = useState("");
    const [direction, setDirection] = useState("");
    const [listOrder, setListOrder] = useState(0);
    const [documentationUrl, setDocumentationUrl] = useState("");
    const [disabled, setDisabled] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [messagesIgnored, setMessagesIgnored] = useState(false);
    const [args, setArgs] = useState<IArgRow[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!integration) return;
        setName(integration.name ?? "");
        setDescription(integration.description ?? "");
        setIntegrationGroup(integration.integration_group ?? "");
        setDirection(normalizeDirection(integration.direction));
        setListOrder(integration.list_order ?? 0);
        setDocumentationUrl(integration.documentation_url ?? "");
        setDisabled(!!integration.disabled);
        setHidden(!!integration.hidden);
        setMessagesIgnored(!!integration.messages_ignored);
        setArgs(
            (integration.arguments ?? []).map((a) => ({
                name: a.name ?? "",
                data_source: a.data_source ?? "",
            })),
        );
    }, [integration]);

    if (!integration) return null;

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        const next: IIntegration = {
            ...integration,
            name: name.trim(),
            description,
            integration_group: integrationGroup.trim(),
            direction,
            list_order: listOrder,
            documentation_url: documentationUrl,
            disabled,
            hidden,
            messages_ignored: messagesIgnored,
            arguments: args.map((a, i) => ({
                ...((integration.arguments ?? [])[i] ?? ({} as IArgument)),
                argument_number: i + 1,
                name: a.name,
                data_source: a.data_source,
                columns: "",
            })) as IArgument[],
            num_args: args.length,
        };
        await onSave(next);
        setSaving(false);
    };

    return (
        <Modal isOpen toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>
                Edit Integration{" "}
                <code className={styles.modalSubtle}>{integration.process}</code>
            </ModalHeader>
            <Form onSubmit={onSubmit}>
                <ModalBody>
                    <div className={styles.modalField}>
                        <Label>Name</Label>
                        <Input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            required
                            minLength={2}
                            autoFocus
                        />
                    </div>

                    <div className={styles.modalField}>
                        <Label>Description</Label>
                        <Input
                            type="textarea"
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.currentTarget.value)}
                        />
                    </div>

                    <div className={styles.modalRow}>
                        <div className={styles.modalField}>
                            <Label>Integration group</Label>
                            <Input
                                type="text"
                                value={integrationGroup}
                                onChange={(e) => setIntegrationGroup(e.currentTarget.value)}
                                list="ei2-existing-groups"
                                autoComplete="off"
                                placeholder="pick existing or type new"
                            />
                            <datalist id="ei2-existing-groups">
                                {existingGroups.map((g) => (
                                    <option key={g} value={g} />
                                ))}
                            </datalist>
                        </div>
                        <div className={styles.modalField}>
                            <Label>Direction</Label>
                            <Input
                                type="select"
                                value={direction}
                                onChange={(e) => setDirection(e.currentTarget.value)}
                            >
                                {DIRECTIONS.map((d) => (
                                    <option key={d} value={d}>
                                        {d === "" ? "(none)" : d}
                                    </option>
                                ))}
                            </Input>
                        </div>
                        <div className={styles.modalField}>
                            <Label>List order</Label>
                            <Input
                                type="number"
                                value={listOrder}
                                onChange={(e) => setListOrder(parseInt(e.currentTarget.value, 10) || 0)}
                            />
                        </div>
                    </div>

                    <div className={styles.modalField}>
                        <Label>Documentation URL</Label>
                        <Input
                            type="url"
                            value={documentationUrl}
                            onChange={(e) => setDocumentationUrl(e.currentTarget.value)}
                            placeholder="https://…"
                        />
                    </div>

                    <div className={styles.modalToggles}>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={disabled}
                                onChange={(e) => setDisabled(e.currentTarget.checked)}
                            />{" "}
                            Disabled
                        </Label>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={hidden}
                                onChange={(e) => setHidden(e.currentTarget.checked)}
                            />{" "}
                            Hidden
                        </Label>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={messagesIgnored}
                                onChange={(e) => setMessagesIgnored(e.currentTarget.checked)}
                            />{" "}
                            Ignore messages
                        </Label>
                    </div>

                    <div className={styles.modalArgs}>
                        <div className={styles.modalArgsHeader}>
                            <span>Arguments</span>
                            <Button
                                type="button"
                                size="sm"
                                color="secondary"
                                onClick={() => setArgs([...args, { name: "", data_source: "" }])}
                            >
                                + Add argument
                            </Button>
                        </div>
                        {args.map((a, i) => {
                            const knownLoader = searchMethods.find(
                                (s) => s.process === a.data_source,
                            );
                            return (
                                <div key={i} className={styles.argRow}>
                                    <Input
                                        type="text"
                                        className={styles.argName}
                                        bsSize="sm"
                                        value={a.name}
                                        placeholder="argument name"
                                        onChange={(e) =>
                                            setArgs(
                                                args.map((row, idx) =>
                                                    idx === i
                                                        ? { ...row, name: e.currentTarget.value }
                                                        : row,
                                                ),
                                            )
                                        }
                                    />
                                    <Input
                                        type="select"
                                        className={styles.argLoader}
                                        bsSize="sm"
                                        value={a.data_source}
                                        onChange={(e) =>
                                            setArgs(
                                                args.map((row, idx) =>
                                                    idx === i
                                                        ? { ...row, data_source: e.currentTarget.value }
                                                        : row,
                                                ),
                                            )
                                        }
                                    >
                                        <option value="">(free text — no loader)</option>
                                        {searchMethods.map((s) => (
                                            <option key={s.process} value={s.process}>
                                                {s.description || s.process}
                                            </option>
                                        ))}
                                        {a.data_source && !knownLoader && (
                                            <option value={a.data_source}>
                                                {a.data_source} (legacy)
                                            </option>
                                        )}
                                    </Input>
                                    <button
                                        type="button"
                                        className={styles.argRemove}
                                        onClick={() => setArgs(args.filter((_, idx) => idx !== i))}
                                        title="Remove this argument"
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" color="primary" disabled={saving}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}
